/** @jsx jsx */
import { memo, type Ref } from 'react';
import { styled, jsx, css } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import ErrorCell from '../error-cell/index.tsx';
import {
	useIsSingleLineRowHeightEnabled,
	useIsMergedColumnVisible,
} from '../../../controllers/features/selectors.tsx';
import type { IconCell$key } from './__generated__/IconCell.graphql';

export type Props = {
	nameOld?: string | null;
	iconUrlOld?: string | null;
	showErrorIconWhenEmpty?: boolean;
	columnId?: string;
	fragmentRef?: IconCell$key | null;
};

export const IconCell = memo(
	({ nameOld, iconUrlOld, showErrorIconWhenEmpty, fragmentRef }: Props) => {
		const data = useFragment<IconCell$key>(
			graphql`
				fragment IconCell on JiraIssueTypeField {
					id
					issueType {
						avatar {
							small
						}
						name
					}
				}
			`,
			fragmentRef,
		);

		const iconUrl = iconUrlOld ?? data?.issueType?.avatar?.small;
		const name = nameOld ?? data?.issueType?.name;

		const isSingleLineRowHeightFeatureEnabled = useIsSingleLineRowHeightEnabled();

		const hoverPopoverRefFactory = isSingleLineRowHeightFeatureEnabled
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data?.id)
			: null;

		let isMergedColumnVisible = false;
		if (fg('jira_list_merged_column_gate')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isMergedColumnVisible = useIsMergedColumnVisible();
		}

		const isSingleLineRowHeightEnabled =
			isSingleLineRowHeightFeatureEnabled && !isMergedColumnVisible;

		if (!name?.length || !iconUrl?.length) {
			return showErrorIconWhenEmpty ? (
				<ErrorCell cellType="icon" reason="Missing required fields" />
			) : null; // aka an empty cell
		}

		const imgComponent = (ref: Ref<HTMLImageElement> | HoverPopoverContentRef | null = null) => (
			<img
				height="16"
				width="16"
				src={iconUrl}
				alt={name}
				data-vc="native-issue-table-ui-icon-cell-img"
				css={isSingleLineRowHeightEnabled && imgStyles}
				ref={ref}
			/>
		);

		if (isSingleLineRowHeightEnabled) {
			let hoverPopoverRef: HoverPopoverContentRef = null;
			if (hoverPopoverRefFactory) {
				hoverPopoverRef = hoverPopoverRefFactory({
					content: name,
					alwaysShow: true,
				});

				return imgComponent(hoverPopoverRef);
			}

			return (
				<HoverPopoverWithContext<HTMLImageElement> content={name}>
					{({ ref }) => imgComponent(ref)}
				</HoverPopoverWithContext>
			);
		}

		return (
			// @ts-expect-error Incompatible ref type in tooltip (AK needs "HTMLElement", Compiled provides "HTMLDivElement" somehow these are not compatible in Typescript)
			<Tooltip content={name} tag={AlignmentFix}>
				{imgComponent()}
			</Tooltip>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AlignmentFix = styled.div({
	display: 'flex',
});

const imgStyles = css({
	display: 'block',
});
